import React from 'react';
import { useSelector } from 'react-redux';
import Form from '../../components/Form';
import Row from '../../components/Row';
import Col from '../../components/Col';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { toast } from 'react-toastify';
import { variables } from 'src/Tenants';
import KeyHandoffSuiteDetails from './KeyHandoff/KeyHandoffSuiteDetails/KeyHandoffSuiteDetails';
import KeyHandoffDocumentsAndNotes from './KeyHandoff/KeyHandoffDocumentsAndNotes/KeyHandoffDocumentsAndNotes';
import KeyHandoffSignatures from './KeyHandoff/KeyHandoffSignatures/KeyHandoffSignatures';
import KeyHandoffKeysAndValues from './KeyHandoff/KeyHandoffKeysAndValues/KeyHandoffKeysAndValues';
import KeyHandoffSubmitBar from './KeyHandoff/KeyHandoffSubmitBar/KeyHandoffSubmitBar';
import KeyHandoffSuccess from './KeyHandoff/KeyHandoffSuccess/KeyHandoffSuccess';
import KeyHandoffSkeletonBody from './skeletons/KeyHandoffSkeletonBody';
import KeyPickupServiceRequestList from 'src/pages/Suite/KeyPickupRequest/KeyPickupServiceRequestList';
import KeyPickupWarrantyDetails from 'src/pages/Suite/KeyPickupWarranty/KeyPickupWarrantyDetails';
import moment from 'moment';
import {
  useKeyHandoffSubmission,
  UseKeyHandoffSubmissionReturn,
  KeyHandoffParams,
} from 'src/SharedCustomHooks/UseSuiteActions/useKeyHandoffSubmission';
import {
  useKeyPickupData,
  UseKeyPickupDataReturn,
} from 'src/SharedCustomHooks/UseSuiteActions/useKeyPickupData';
import { Suite } from 'src/SharedCustomHooks/UseSuiteActions/types';
import {
  KeyPickupFormValues,
  useKeyPickupFormState,
  UseKeyPickupFormStateReturn,
} from './KeyHandoff/useKeyPickupFormState';
import 'react-toastify/dist/ReactToastify.css';
import './keyhandoff.scss';
import './key-handoff-component.scss';

interface KeyhandoffProps {
  onClose: any;
  suiteFormData?: Suite;
  calComEventSummary?: React.ReactNode;
}

const KeyHandoffComponent = ({
  onClose,
  suiteFormData,
  calComEventSummary,
}: KeyhandoffProps) => {
  const user: any = useSelector(selectAuthenticatedUser);

  const {
    submitKeyHandoff,
    isSubmitting,
    isSuccess,
  }: UseKeyHandoffSubmissionReturn = useKeyHandoffSubmission();

  const { serviceRequests, importantDocuments }: UseKeyPickupDataReturn =
    useKeyPickupData(suiteFormData?.id);

  const {
    values,
    canSubmit,
    setNotes,
    setWarrantyStartDate,
    setSignature,
    clearSignature,
  }: UseKeyPickupFormStateReturn = useKeyPickupFormState();

  const {
    internalSignature,
    homeOwnerSignature,
    notes,
    warrantyStartDate,
  }: KeyPickupFormValues = values;

  const handleSignature = (
    image: any,
    signType: any,
    signer: 'internal' | 'homeOwner'
  ) => {
    if (image === null) {
      return toast.error('🚫 Please enter a valid signature.', {
        autoClose: 10000,
        position: 'top-center',
      });
    }

    setSignature(signer, { signature: image, signType });
  };

  const removeInternalSignature = () => {
    clearSignature('internal');
  };

  const removeHomeOwnerSignature = () => {
    clearSignature('homeOwner');
  };

  const handleSubmit = async () => {
    const formattedWarrantyDate: string = warrantyStartDate
      ? moment(warrantyStartDate).format('YYYY-MM-DD')
      : '';

    const params: KeyHandoffParams = {
      suiteId: suiteFormData.id,
      notes,
      warrantyStartDate: formattedWarrantyDate,
      internalSignature,
      homeOwnerSignature,
    };

    await submitKeyHandoff(params);
  };

  const primaryContact: any = suiteFormData?.primaryContact;

  const primaryHomeOwnerName: string = primaryContact?.id
    ? `${primaryContact.firstname} ${primaryContact.lastname}`
    : '';

  const homeOwnerProfileImage: string =
    primaryContact?.profileImage || '/assets/avatar.svg';

  const internalSignerName: string = `${user?.firstName ?? ''} ${
    user?.lastName ?? ''
  }`.trim();

  if (isSuccess) {
    return <KeyHandoffSuccess onBackToSuite={onClose} />;
  }

  return (
    <Form
      headline={'Key Pickup'}
      onClose={onClose}
      onExit={onClose}
      component={
        suiteFormData && suiteFormData.id ? (
          <Row>
            {calComEventSummary && !isSubmitting && (
              <Col xs="12" md="12">
                {calComEventSummary}
              </Col>
            )}
            <Col xs="12" md="12">
              {isSubmitting ? (
                <KeyHandoffSkeletonBody />
              ) : (
                <>
                  <KeyHandoffSuiteDetails
                    suiteFormData={suiteFormData}
                    primaryHomeOwnerName={primaryHomeOwnerName}
                    homeOwnerProfileImage={homeOwnerProfileImage}
                  />
                  {suiteFormData.suiteKeysAndValues &&
                    suiteFormData.suiteKeysAndValues.length > 0 && (
                      <KeyHandoffKeysAndValues
                        items={suiteFormData.suiteKeysAndValues}
                      />
                    )}
                  {/* Warranty Details with editable warranty start date */}
                  <KeyPickupWarrantyDetails
                    suiteId={suiteFormData.id}
                    warrantyStartDate={warrantyStartDate}
                    onWarrantyStartDateChange={setWarrantyStartDate}
                  />
                  {/* Service Requests - only show if they exist. */}
                  {serviceRequests && serviceRequests.length > 0 && (
                    <KeyPickupServiceRequestList suiteId={suiteFormData.id} />
                  )}
                  <KeyHandoffDocumentsAndNotes
                    importantDocuments={importantDocuments}
                    user={user}
                    notes={notes}
                    onChangeNotes={setNotes}
                  />
                  <KeyHandoffSignatures
                    internalSignerName={internalSignerName}
                    primaryHomeOwnerName={primaryHomeOwnerName}
                    internalSignature={internalSignature}
                    homeOwnerSignature={homeOwnerSignature}
                    primaryContact={primaryContact}
                    user={user}
                    onSign={handleSignature}
                    onRemoveInternal={removeInternalSignature}
                    onRemoveHomeOwner={removeHomeOwnerSignature}
                  />
                </>
              )}
              <KeyHandoffSubmitBar
                isSubmitting={isSubmitting}
                disabled={!canSubmit}
                onSubmit={handleSubmit}
              />
            </Col>
          </Row>
        ) : (
          <div className="key-handoff-fetch-error">
            {variables.Suite} details fetching failed.
          </div>
        )
      }
      actions={undefined}
      footer={undefined}
      onSubmit={undefined}
      strapline={undefined}
    />
  );
};

export default KeyHandoffComponent;
